import React from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  loading?: boolean;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
}

const VARIANT: Record<Variant, string> = {
  primary: [
    'bg-brand-500 text-white border-brand-500',
    'hover:bg-brand-600 hover:border-brand-600',
    'shadow-sm-warm dark:shadow-sm-dark',
  ].join(' '),
  secondary: [
    'bg-surface dark:bg-surface-dark',
    'text-ink-title dark:text-ink-title-dark',
    'border-line dark:border-line-dark',
    'hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
    'shadow-sm-warm dark:shadow-sm-dark',
  ].join(' '),
  ghost: [
    'bg-transparent border-transparent',
    'text-ink-text dark:text-ink-text-dark',
    'hover:bg-brand-50 dark:hover:bg-[rgba(203,106,30,0.15)]',
    'hover:text-brand-700 dark:hover:text-brand-300',
  ].join(' '),
  outline: [
    'bg-transparent',
    'text-brand-700 dark:text-brand-300',
    'border-brand-200 dark:border-[rgba(203,106,30,0.40)]',
    'hover:bg-brand-50 dark:hover:bg-[rgba(203,106,30,0.10)]',
  ].join(' '),
  danger: [
    'bg-danger-bg dark:bg-danger-dark-bg',
    'text-danger dark:text-danger-dark',
    'border-[rgba(196,50,28,0.25)]',
    'hover:bg-[rgba(196,50,28,0.15)] dark:hover:bg-[rgba(196,50,28,0.25)]',
  ].join(' '),
};

const SIZE: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5',
  md: 'h-[34px] px-3.5 text-[13px] gap-1.5',
  lg: 'h-[42px] px-5 text-sm gap-2',
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={[
        'inline-flex items-center justify-center font-medium rounded-lg border',
        'whitespace-nowrap transition-all duration-150 cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANT[variant],
        SIZE[size],
        className,
      ].join(' ')}
    >
      {loading ? (
        <svg className="animate-spin w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : icon ? (
        <span className="flex-shrink-0 flex">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}

export function IconButton({
  children,
  active = false,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      {...props}
      className={[
        'w-8 h-8 inline-flex items-center justify-center rounded-lg border transition-all duration-150 cursor-pointer',
        active
          ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
          : 'bg-transparent text-ink-subtitle dark:text-ink-subtitle-dark border-transparent hover:bg-surface-alt dark:hover:bg-surface-dark-alt hover:text-ink-text dark:hover:text-ink-text-dark',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}
